import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ApiError, api } from '../../lib/api';
import { homeRouteFor, useAuth } from '../../lib/auth';
import { Button, Callout, Field, Input } from '../../components/ui';

export function ChangePasswordPage() {
  const { user, status, signOut } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'anonymous') return <Navigate to="/login" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await api.post('/api/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      });
      // The server revokes every session on a password change, so sign in again.
      await signOut();
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.details?.map((d) => d.message).join(' ') || caught.message
          : 'Could not change your password. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const skip = user && !user.mustChangePassword;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-steel-900 px-4 py-10">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 shadow-xl"
      >
        <div>
          <h1 className="text-lg font-bold text-steel-900">Choose your own password</h1>
          <p className="mt-1 text-sm text-steel-500">
            {user?.mustChangePassword
              ? 'Your account was set up with a password someone else knows. Choose a new one so only you can sign in.'
              : 'Update the password for your ODR account.'}
          </p>
        </div>

        {error ? <Callout tone="alarm">{error}</Callout> : null}

        <Field label="Current password" required>
          <Input
            type="password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>

        <Field
          label="New password"
          required
          hint="At least 10 characters. A short phrase you will remember works well."
        >
          <Input
            type="password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <Field label="Repeat new password" required>
          <Input
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <Button type="submit" size="lg" fullWidth loading={busy}>
          Save and sign in again
        </Button>

        {skip ? (
          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={() => navigate(homeRouteFor(user))}
          >
            Cancel
          </Button>
        ) : null}
      </form>
    </div>
  );
}
